import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Modal, InputNumber, Input } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getStockTakeDetailApi, updateStockTakeApi, completeStockTakeApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import useStickyIdentity from '../../components/useStickyIdentity';

const StockTakeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed.
  const [modal, modalContextHolder] = Modal.useModal();

  const [stockTake, setStockTake] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [editedItems, setEditedItems] = useState({});
  const [search, setSearch] = useState('');

  const allItems = useMemo(() => stockTake?.items || [], [stockTake]);

  // Matches item name and size code together, so "shirt m" narrows to one size
  // of one item. Declared above the loading early-return below to keep the
  // hook order stable across renders.
  const filteredItems = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return allItems;
    return allItems.filter((item) => {
      const haystack = `${item.uniform_item_name || ''} ${item.size_code || ''}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [allItems, search]);

  const fetchData = () => {
    setLoading(true);
    getStockTakeDetailApi(id).then(response => {
      setStockTake(response.data?.data || null);
      setEditedItems({});
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management/stock-take'); });
  };

  // The route reuses this component across ids, so clear the filter alongside
  // the fetch — otherwise a search typed for one stock take silently hides rows
  // of the next one.
  useEffect(() => { setSearch(''); fetchData(); }, [id]);

  const handlePhysicalQtyChange = (itemId, value) => {
    setEditedItems(prev => ({ ...prev, [itemId]: value }));
  };

  const getPhysicalQty = (item) => {
    return editedItems[item.id] !== undefined ? editedItems[item.id] : item.physical_quantity;
  };

  const getVariance = (item) => {
    const physical = getPhysicalQty(item);
    return physical - item.system_quantity;
  };

  const handleSave = () => {
    const items = Object.entries(editedItems).map(([itemId, qty]) => ({
      id: parseInt(itemId),
      physical_quantity: qty
    }));
    if (items.length === 0) {
      toast.info('No changes to save', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setSaving(true);
    updateStockTakeApi(id, { items }).then(response => {
      setStockTake(response.data?.data || stockTake);
      setEditedItems({});
      setSaving(false);
      toast.success('Counts saved', { position: "top-right", autoClose: 3000, transition: Bounce });
    }).catch(() => {
      setSaving(false);
      toast.error('Failed to save counts', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleComplete = () => {
    modal.confirm({
      title: 'Complete Stock Take',
      centered: true,
      content: 'This will adjust inventory to match physical counts. This action is irreversible. Continue?',
      okText: 'Complete',
      okType: 'danger',
      onOk: () => {
        setCompleting(true);
        completeStockTakeApi(id).then(() => {
          toast.success('Stock take completed, inventory adjusted', { position: "top-right", autoClose: 3000, transition: Bounce });
          setCompleting(false);
          fetchData();
        }).catch(() => {
          setCompleting(false);
          toast.error('Failed to complete stock take', { position: "top-right", autoClose: 3000, transition: Bounce });
        });
      }
    });
  };

  // Above the loading guard below: a hook cannot sit after an early return.
  const pinLeft = useStickyIdentity();

  if (loading || !stockTake) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const isInProgress = stockTake.status === 'IN_PROGRESS';
  const hasChanges = Object.keys(editedItems).length > 0;
  // Edits survive filtering — handleSave posts every entry in editedItems, not
  // just the visible ones — so count the ones the current search has hidden.
  const visibleIds = new Set(filteredItems.map((item) => item.id));
  const hiddenEditedCount = Object.keys(editedItems)
    .filter((itemId) => !visibleIds.has(Number(itemId))).length;

  const text = (v) => (v === null || v === undefined ? '' : String(v));
  // An uncounted row carries a null quantity, and InputNumber hands back null
  // when its field is emptied. Left raw these produce NaN comparisons, which
  // antd cannot order consistently — so coerce before subtracting.
  const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
  // Size codes are as often numeric ("38", "40") as lettered, so collate
  // numerically to keep 40 after 9 rather than before it.
  const compareSize = (a, b) =>
    text(a.size_code).localeCompare(text(b.size_code), undefined, { numeric: true });

  const columns = [
    {
      // Pinned: the counter reads across to Physical Qty and Variance, and
      // must not lose which item the numbers belong to.
      title: 'Uniform Item',
      dataIndex: 'uniform_item_name',
      defaultSortOrder: 'ascend',
      ...pinLeft(220),
      // Ties break on size so an item's rows stay in a stable, readable order
      // instead of whatever order the API happened to return them in.
      sorter: (a, b) =>
        text(a.uniform_item_name).localeCompare(text(b.uniform_item_name)) || compareSize(a, b),
    },
    { title: 'Size', dataIndex: 'size_code', sorter: compareSize },
    {
      title: 'System Qty',
      dataIndex: 'system_quantity',
      sorter: (a, b) => num(a.system_quantity) - num(b.system_quantity),
    },
    {
      title: 'Physical Qty',
      // Sorts on the edited value, not the saved one, so the order always
      // matches what is on screen.
      sorter: (a, b) => num(getPhysicalQty(a)) - num(getPhysicalQty(b)),
      render: (_, record) => isInProgress ? (
        <InputNumber
          min={0}
          value={getPhysicalQty(record)}
          onChange={(val) => handlePhysicalQtyChange(record.id, val)}
          style={{ width: 80 }}
        />
      ) : record.physical_quantity
    },
    {
      title: 'Variance',
      // Mirrors the render below: live variance while counting, the stored
      // value once the take is closed.
      sorter: (a, b) => (isInProgress
        ? num(getVariance(a)) - num(getVariance(b))
        : num(a.variance) - num(b.variance)),
      render: (_, record) => {
        const variance = isInProgress ? getVariance(record) : record.variance;
        return (
          <span style={{
            color: variance < 0 ? '#ff4d4f' : variance > 0 ? '#52c41a' : 'inherit',
            fontWeight: variance !== 0 ? 600 : 400
          }}>
            {variance > 0 ? `+${variance}` : variance}
          </span>
        );
      }
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {modalContextHolder}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
<h3 className="d-inline">Stock Take - {stockTake.warehouse_name}</h3>
          </div>
          <span style={getStatusBadge(stockTake.status)}>
            {stockTake.status.replace('_', ' ')}
          </span>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-2"><strong>Conducted By:</strong> {stockTake.conducted_by_name}</div>
              <div className="col-md-3 mb-2"><strong>Date:</strong> {formatDateHandler(stockTake.conducted_date)}</div>
              <div className="col-md-3 mb-2"><strong>Items:</strong> {stockTake.item_count}</div>
              <div className="col-md-3 mb-2">
                <strong>Total Variance:</strong>{' '}
                <span style={{ color: stockTake.total_variance < 0 ? '#ff4d4f' : stockTake.total_variance > 0 ? '#52c41a' : 'inherit', fontWeight: 600 }}>
                  {stockTake.total_variance > 0 ? `+${stockTake.total_variance}` : stockTake.total_variance}
                </span>
              </div>
              {stockTake.notes && (
                <div className="col-md-12 mb-2"><strong>Notes:</strong> {stockTake.notes}</div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0">Count Items</h5>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Input allowClear value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search item or size" prefix={<i className="fa fa-search text-muted" />}
                style={{ width: 240 }} aria-label="Search count items by uniform item or size" />
              {isInProgress && (
                <>
                  <Button label="Save Counts" icon="fa fa-save" className="btn btn-primary btn-sm"
                    loading={saving} disabled={!hasChanges || saving} onClick={handleSave} />
                  <Button label="Complete Stock Take" icon="fa fa-check" className="btn btn-success btn-sm"
                    loading={completing} disabled={completing} onClick={handleComplete} />
                </>
              )}
            </div>
          </div>
          <div className="card-body">
            {/* A filter hides rows without discarding their edits, so say plainly
                how many rows are out of view and whether any hidden row is
                already counted — otherwise Save appears to write invisible data. */}
            {search.trim() !== '' && (
              <div className="mb-2 text-muted small">
                Showing {filteredItems.length} of {allItems.length} rows.
                {hiddenEditedCount > 0 && (
                  <span className="text-warning ms-1">
                    {hiddenEditedCount} unsaved {hiddenEditedCount === 1 ? 'count is' : 'counts are'} hidden by this search and will still be saved.
                  </span>
                )}
              </div>
            )}
            <Table columns={columns} dataSource={filteredItems} rowKey={(r) => r.id}
              locale={{ emptyText: search.trim() ? 'No items match your search' : 'No items' }}
              pagination={false} scroll={{ x: "max-content" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockTakeDetail;
